import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

interface CulturalData {
  [culture: string]: number;
}

const CulturalSelectionsChart: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<CulturalData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCulturalData();
  }, []);

  const fetchCulturalData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/charts/cultural-selections', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch cultural data: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching cultural data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  // Generate vibrant colors for the pie chart
  const generateColors = (count: number) => {
    const baseColors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
      '#8BC34A', '#E91E63', '#673AB7', '#009688', '#FF5722',
      '#795548', '#607D8B', '#FFC107', '#3F51B5', '#CDDC39'
    ];
    
    return Array(count).fill(0).map((_, index) => baseColors[index % baseColors.length]);
  };

  const chartData = {
    labels: Object.keys(data),
    datasets: [
      {
        label: 'Students',
        data: Object.values(data),
        backgroundColor: generateColors(Object.keys(data).length),
        borderColor: generateColors(Object.keys(data).length).map(color => color.replace('0.8', '1')),
        borderWidth: 2,
        hoverOffset: 20,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
          },
          color: '#2c3e50',
        },
      },
      title: {
        display: true,
        text: 'Cultural Selections Distribution',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#2c3e50',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function(context: any) {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.raw / total) * 100).toFixed(1);
            return `${context.label}: ${context.raw} students (${percentage}%)`;
          }
        }
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 2000,
      easing: 'easeInOutCubic' as const,
    },
    interaction: {
      intersect: false,
      mode: 'nearest' as const,
    },
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading cultural selections data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>Error loading chart: {error}</p>
        <button onClick={fetchCulturalData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return (
      <div className="chart-empty">
        <p>No cultural selection data available</p>
        <p className="chart-empty-detail">Students with cultural selections will appear here</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Pie data={chartData} options={options} />
      </div>
    </div>
  );
};

export default CulturalSelectionsChart;
